import React, { useState, useMemo } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import type { EvaluationResult } from '../evaluations/evaluationTypes'
import { TrendingUp, AlertTriangle, GitCompare, Info } from 'lucide-react'

export interface LongitudinalEvolutionViewProps {
  athleteId: string
  athleteName: string
  bodyEvaluations: EvaluationResult[]
  performanceEvaluations: EvaluationResult[]
  from?: string
  to?: string
}

interface MetricDefinition {
  code: string
  label: string
  unit: string
  category: 'performance' | 'body'
  direction: 'lower' | 'higher' | 'neutral'
  description?: string
}

const METRIC_DEFINITIONS: MetricDefinition[] = [
  // Rendimiento físico
  { code: 'SPRINT40', label: 'Sprint 40 yardas', unit: 's', category: 'performance', direction: 'lower', description: 'Velocidad máxima lanzada' },
  { code: 'SPRINT10', label: 'Sprint 10 yardas', unit: 's', category: 'performance', direction: 'lower', description: 'Aceleración y primer paso' },
  { code: 'CONE3', label: '3-Cone Agilidad', unit: 's', category: 'performance', direction: 'lower', description: 'Cambio de dirección y agilidad reactiva' },
  { code: 'JUMP', label: 'Salto Horizontal', unit: 'cm', category: 'performance', direction: 'higher', description: 'Potencia explosiva del tren inferior' },

  // Medidas corporales
  { code: 'WEIGHT', label: 'Peso', unit: 'kg', category: 'body', direction: 'neutral', description: 'Masa corporal total en báscula calibrada' },
  { code: 'FAT', label: 'Porcentaje Graso', unit: '%', category: 'body', direction: 'neutral', description: 'Estimación de composición por bioimpedancia o pliegues' },
  { code: 'MUSCLE', label: 'Masa Muscular', unit: 'kg', category: 'body', direction: 'neutral', description: 'Masa magra muscular estimada' },
  { code: 'HEIGHT', label: 'Altura', unit: 'cm', category: 'body', direction: 'neutral', description: 'Talla de pie con estadiómetro' },
]

export const LongitudinalEvolutionView: React.FC<LongitudinalEvolutionViewProps> = ({
  athleteId,
  athleteName,
  bodyEvaluations,
  performanceEvaluations,
  from,
  to,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<'performance' | 'body'>('performance')

  // Métricas disponibles para la categoría seleccionada
  const availableMetrics = useMemo(() => {
    return METRIC_DEFINITIONS.filter((m) => m.category === selectedCategory)
  }, [selectedCategory])

  const [selectedMetricCode, setSelectedMetricCode] = useState<string>('SPRINT40')

  // Asegurar que la métrica seleccionada pertenece a la categoría activa
  const activeMetric = useMemo(() => {
    const match = availableMetrics.find((m) => m.code === selectedMetricCode)
    if (match) return match
    return availableMetrics[0] || METRIC_DEFINITIONS[0]
  }, [availableMetrics, selectedMetricCode])

  // Puntos de comparación interactivos seleccionados por el usuario
  const [highlightedPointA, setHighlightedPointA] = useState<string | null>(null)
  const [highlightedPointB, setHighlightedPointB] = useState<string | null>(null)

  // Filtrar y procesar datos longitudinales para la métrica activa
  const evaluationsPool = selectedCategory === 'performance' ? performanceEvaluations : bodyEvaluations

  const processedData = useMemo(() => {
    const matching = evaluationsPool
      .filter((e) => {
        if (e.athlete_id !== athleteId) return false
        if (e.protocol_code !== activeMetric.code) return false
        if (e.representative === null || e.representative === undefined) return false
        if (from && e.measured_at && e.measured_at.slice(0, 10) < from) return false
        if (to && e.measured_at && e.measured_at.slice(0, 10) > to) return false
        return true
      })
      .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime())

    return matching.map((e, idx) => {
      const dateObj = new Date(e.measured_at)
      const readableDate = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
      const method = e.method || e.protocol?.method || 'Estándar'
      const device = e.device || 'Dispositivo calibrado'
      const camp = e.camp_name || e.session_name || `Sesión ${idx + 1}`

      return {
        id: e.id,
        index: idx,
        date: readableDate,
        rawDate: e.measured_at,
        value: Number(e.representative),
        unit: e.protocol?.unit || activeMetric.unit,
        protocol: e.protocol?.name || activeMetric.label,
        protocolCode: e.protocol_code,
        method,
        device,
        camp,
        label: `${readableDate}`,
        fullLabel: `${readableDate} · ${camp}`,
      }
    })
  }, [evaluationsPool, athleteId, activeMetric, from, to])

  // Detección de incompatibilidades en métodos/protocolos
  const methodVariations = useMemo(() => {
    const methods = Array.from(new Set(processedData.map((d) => d.method)))
    return methods
  }, [processedData])

  const hasIncompatibleMethods = methodVariations.length > 1

  // Cálculo de estadísticas compactas (primer valor, último valor, diferencia)
  const statsSummary = useMemo(() => {
    if (processedData.length === 0) return null
    const first = processedData[0]
    const last = processedData[processedData.length - 1]
    const diff = Number((last.value - first.value).toFixed(2))
    const pct = first.value !== 0 ? Number(((diff / first.value) * 100).toFixed(1)) : 0

    return {
      first,
      last,
      diff,
      pct,
      count: processedData.length,
    }
  }, [processedData])

  // Comparación destacada directa (Punto A vs Punto B)
  const directComparison = useMemo(() => {
    if (!highlightedPointA || !highlightedPointB || highlightedPointA === highlightedPointB) {
      // Si hay exactamente 2 mediciones en el histórico y no hay selección manual, comparar esas 2 por defecto
      if (processedData.length === 2 && !highlightedPointA && !highlightedPointB) {
        const pA = processedData[0]
        const pB = processedData[1]
        const diff = Number((pB.value - pA.value).toFixed(2))
        const pct = pA.value !== 0 ? Number(((diff / pA.value) * 100).toFixed(1)) : 0
        const isCompatible = pA.method === pB.method
        return { pA, pB, diff, pct, isCompatible }
      }
      return null
    }

    const pA = processedData.find((d) => d.id === highlightedPointA)
    const pB = processedData.find((d) => d.id === highlightedPointB)
    if (!pA || !pB) return null

    // Ordenar cronológicamente A antes que B
    const [early, late] = new Date(pA.rawDate).getTime() <= new Date(pB.rawDate).getTime() ? [pA, pB] : [pB, pA]
    const diff = Number((late.value - early.value).toFixed(2))
    const pct = early.value !== 0 ? Number(((diff / early.value) * 100).toFixed(1)) : 0
    const isCompatible = early.method === late.method

    return {
      pA: early,
      pB: late,
      diff,
      pct,
      isCompatible,
    }
  }, [processedData, highlightedPointA, highlightedPointB])

  const handlePointClick = (pointId: string) => {
    if (!highlightedPointA) {
      setHighlightedPointA(pointId)
    } else if (!highlightedPointB && pointId !== highlightedPointA) {
      setHighlightedPointB(pointId)
    } else if (pointId === highlightedPointA) {
      setHighlightedPointA(null)
    } else if (pointId === highlightedPointB) {
      setHighlightedPointB(null)
    } else {
      // Reemplazar Punto B
      setHighlightedPointB(pointId)
    }
  }

  return (
    <div
      style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}
    >
      {/* Cabecera y selectores de categoría y métrica */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.85rem',
          marginBottom: '1rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={18} color="var(--brand-red, #b91c1c)" />
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Mi Evolución Longitudinal
            </h3>
          </div>
          <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
            Evolución temporal de pruebas físicas y medidas corporales de {athleteName}
          </p>
        </div>

        {/* Controles: Categoría (Segmented control) y Métrica (Dropdown) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          {/* Segmented Control de Categoría */}
          <div
            style={{
              display: 'inline-flex',
              background: 'var(--surface-subtle, #f1f5f9)',
              borderRadius: '6px',
              padding: '2px',
              border: '1px solid #e2e8f0',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('performance')
                setSelectedMetricCode('SPRINT40')
                setHighlightedPointA(null)
                setHighlightedPointB(null)
              }}
              style={{
                padding: '4px 10px',
                fontSize: '0.75rem',
                fontWeight: selectedCategory === 'performance' ? 700 : 500,
                borderRadius: '4px',
                border: 'none',
                background: selectedCategory === 'performance' ? '#ffffff' : 'transparent',
                color: selectedCategory === 'performance' ? '#0f172a' : '#64748b',
                cursor: 'pointer',
                boxShadow: selectedCategory === 'performance' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              Rendimiento Físico
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('body')
                setSelectedMetricCode('WEIGHT')
                setHighlightedPointA(null)
                setHighlightedPointB(null)
              }}
              style={{
                padding: '4px 10px',
                fontSize: '0.75rem',
                fontWeight: selectedCategory === 'body' ? 700 : 500,
                borderRadius: '4px',
                border: 'none',
                background: selectedCategory === 'body' ? '#ffffff' : 'transparent',
                color: selectedCategory === 'body' ? '#0f172a' : '#64748b',
                cursor: 'pointer',
                boxShadow: selectedCategory === 'body' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              Medidas Corporales
            </button>
          </div>

          {/* Selector de Métrica con Etiquetas Humanas */}
          <select
            data-testid="evolution-metric-selector"
            aria-label="Seleccionar métrica de evolución"
            value={activeMetric.code}
            onChange={(e) => {
              setSelectedMetricCode(e.target.value)
              setHighlightedPointA(null)
              setHighlightedPointB(null)
            }}
            style={{
              padding: '5px 10px',
              fontSize: '0.78rem',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#0f172a',
            }}
          >
            {availableMetrics.map((m) => (
              <option key={m.code} value={m.code}>
                {m.label} ({m.unit})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumen Compacto Longitudinal */}
      {statsSummary && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '8px',
            marginBottom: '1rem',
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '10px 12px',
          }}
        >
          <div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Primer registro</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {statsSummary.first.value} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>{activeMetric.unit}</span>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{statsSummary.first.date}</div>
          </div>

          <div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Último registro</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {statsSummary.last.value} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>{activeMetric.unit}</span>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{statsSummary.last.date}</div>
          </div>

          <div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Diferencia acumulada</div>
            {statsSummary.count >= 2 ? (
              <>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    color:
                      activeMetric.direction === 'lower'
                        ? statsSummary.diff < 0
                          ? '#15803d'
                          : statsSummary.diff > 0
                          ? '#d97706'
                          : '#64748b'
                        : activeMetric.direction === 'higher'
                        ? statsSummary.diff > 0
                          ? '#15803d'
                          : statsSummary.diff < 0
                          ? '#b91c1c'
                          : '#64748b'
                        : '#334155', // Medidas corporales en tono neutro
                  }}
                >
                  {statsSummary.diff > 0 ? `+${statsSummary.diff}` : statsSummary.diff}{' '}
                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{activeMetric.unit}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, marginLeft: '4px' }}>
                    ({statsSummary.pct > 0 ? `+${statsSummary.pct}` : statsSummary.pct}%)
                  </span>
                </div>
                <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                  {activeMetric.direction === 'lower' && statsSummary.diff < 0
                    ? 'Reducción de tiempo (mejora bajo condiciones comparables)'
                    : activeMetric.direction === 'lower' && statsSummary.diff > 0
                    ? 'Incremento de tiempo registrado'
                    : activeMetric.direction === 'higher' && statsSummary.diff > 0
                    ? 'Incremento de rendimiento'
                    : 'Variación en el periodo'}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#64748b' }}>Primera medición</div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Se requieren 2+ registros para calcular evolución</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Alerta de protocolo o método incompatible si aplica */}
      {hasIncompatibleMethods && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            background: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '0.75rem',
            color: '#92400e',
            marginBottom: '0.85rem',
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Instrumental y métodos variables detectados:</strong> Los registros combinan diferentes metodologías ({methodVariations.join(', ')}). Los puntos se visualizan de forma independiente para evitar falsas comparaciones lineales.
          </div>
        </div>
      )}

      {/* Visualización Gráfica o Estado Vacío */}
      {processedData.length > 0 ? (
        <div style={{ width: '100%', height: '240px', marginTop: '0.5rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={processedData} margin={{ top: 15, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis
                domain={['dataMin - 0.5', 'dataMax + 0.5']}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div
                        style={{
                          background: '#0a1320',
                          color: '#ffffff',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
                        }}
                      >
                        <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: '2px' }}>
                          {data.value} {data.unit}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                          {data.protocol} · {data.date}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem', marginTop: '2px' }}>
                          {data.camp}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '0.68rem', marginTop: '3px' }}>
                          Método: {data.method} ({data.device})
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={selectedCategory === 'performance' ? 'var(--brand-red, #b91c1c)' : '#0284c7'}
                strokeWidth={2}
                strokeDasharray={hasIncompatibleMethods ? '4 4' : undefined}
                dot={(props: any) => {
                  const { cx, cy, payload } = props
                  const isHighlightedA = highlightedPointA === payload.id
                  const isHighlightedB = highlightedPointB === payload.id
                  const isHighlighted = isHighlightedA || isHighlightedB

                  return (
                    <circle
                      key={payload.id}
                      cx={cx}
                      cy={cy}
                      r={isHighlighted ? 7 : 4}
                      fill={isHighlighted ? '#f59e0b' : selectedCategory === 'performance' ? '#b91c1c' : '#0284c7'}
                      stroke="#ffffff"
                      strokeWidth={isHighlighted ? 3 : 2}
                      style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                      onClick={() => handlePointClick(payload.id)}
                    />
                  )
                }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div
          style={{
            padding: '2.5rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted, #64748b)',
            background: 'var(--surface-subtle, #f8fafc)',
            borderRadius: '8px',
            fontSize: '0.82rem',
          }}
        >
          <Info size={24} style={{ margin: '0 auto 8px', color: '#94a3b8' }} />
          <div style={{ fontWeight: 600, color: '#334155', marginBottom: '2px' }}>
            Sin registros de «{activeMetric.label}» en este periodo
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
            Las evaluaciones registradas en concentraciones del equipo se reflejarán automáticamente aquí.
          </div>
        </div>
      )}

      {/* Selector de Puntos para Comparación Directa */}
      {processedData.length >= 2 && (
        <div style={{ marginTop: '0.85rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>
              <GitCompare size={14} color="#0284c7" />
              <span>Comparar dos evaluaciones:</span>
            </div>
            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
              Haz clic en dos fechas para comparar directamente
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {processedData.map((d) => {
              const isA = highlightedPointA === d.id
              const isB = highlightedPointB === d.id
              const isSelected = isA || isB

              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => handlePointClick(d.id)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.72rem',
                    borderRadius: '4px',
                    border: isSelected ? '1px solid #d97706' : '1px solid #e2e8f0',
                    background: isSelected ? '#fef3c7' : '#f8fafc',
                    color: isSelected ? '#92400e' : '#334155',
                    fontWeight: isSelected ? 700 : 500,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>{d.date}</span>
                  <span style={{ fontWeight: 700 }}>
                    ({d.value} {d.unit})
                  </span>
                  {isA && <span style={{ fontSize: '0.65rem', background: '#d97706', color: '#fff', padding: '1px 4px', borderRadius: '2px' }}>A</span>}
                  {isB && <span style={{ fontSize: '0.65rem', background: '#d97706', color: '#fff', padding: '1px 4px', borderRadius: '2px' }}>B</span>}
                </button>
              )
            })}
          </div>

          {/* Resultado de la comparación destacada */}
          {directComparison && (
            <div
              style={{
                marginTop: '8px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '8px 12px',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.78rem',
              }}
            >
              <div>
                <strong>{directComparison.pA.camp} ({directComparison.pA.date})</strong>: {directComparison.pA.value} {directComparison.pA.unit}{' '}
                ➔ <strong>{directComparison.pB.camp} ({directComparison.pB.date})</strong>: {directComparison.pB.value} {directComparison.pB.unit}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    color:
                      activeMetric.direction === 'lower'
                        ? directComparison.diff < 0
                          ? '#15803d'
                          : '#d97706'
                        : activeMetric.direction === 'higher'
                        ? directComparison.diff > 0
                          ? '#15803d'
                          : '#b91c1c'
                        : '#334155',
                  }}
                >
                  {directComparison.diff > 0 ? `+${directComparison.diff}` : directComparison.diff} {activeMetric.unit}{' '}
                  ({directComparison.pct > 0 ? `+${directComparison.pct}` : directComparison.pct}%)
                </span>

                {!directComparison.isCompatible && (
                  <span
                    style={{
                      fontSize: '0.68rem',
                      background: '#fef3c7',
                      color: '#92400e',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontWeight: 600,
                    }}
                  >
                    Métodos distintos ({directComparison.pA.method} vs {directComparison.pB.method})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LongitudinalEvolutionView
